import { CircularProgress, Divider } from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import Footer from "../../components/layout/Footer.tsx";
import NavBar from "../../components/layout/NavBar.tsx";
import { ATTRIBUTE_TYPES } from "../../components/patient/categorize/CategorizeUtils.tsx";
import ProjectHeader from "../../components/project/ProjectHeader.tsx";
import ProjectLockedBanner from "../../components/project/ProjectLockedBanner.tsx";
import {
  PatientContext,
  PatientDispatchContext,
  patientReducer,
} from "../../contexts/PatientContext.tsx";
import { ProjectDispatchContext } from "../../contexts/ProjectContext.tsx";
import { emptyPatientContext } from "../../models/patient.ts";
import { convertProjectToProjectContext } from "../../models/Project.ts";
import { useFetch } from "../../services/hooks/useFetch.ts";
import { projectsPrefix } from "../../services/ProjectsServices.ts";
import { apiPrefix } from "../../utils/main.ts";
import Overview from "./Overview.js";
import Patients from "./Patients.tsx";

export default function ProjectDetails() {
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patientId");
  const projectId = searchParams.get("projectId");
  const dispatch = useContext(ProjectDispatchContext);

  const [showCodeUpdateFailureAlert, setShowCodeUpdateFailureAlert] =
    useState(false);
  const [showPollingTimeoutAlert, setShowPollingTimeoutAlert] = useState(false);
  const [disableRefineButton, setDisableRefineButton] = useState(true);
  const pollTimer = useRef<NodeJS.Timeout>(undefined);

  const [selectedTab, setSelectedTab] = useState(patientId ? 1 : 0);

  const {
    data: project,
    fetch: fetchProject,
    isLoading: projectLoading,
  } = useFetch<ProjectContextType, undefined, Project>();

  const {
    data: projectSummaryData,
    fetch: fetchProjectSummary,
    error: projectSummaryError,
    isLoading: projectSummaryLoading,
    reset: resetProjectSummary,
  } = useFetch<ProjectSummary>();

  const {
    fetch: fetchSummaries,
    isLoading: summariesLoading,
    status: summariesStatus,
  } = useFetch<InternalPatientSummary[]>();

  const [projectSummary, setProjectSummary] = useState<
    Partial<DeviceSearchResponse>
  >({});
  const [patientSummaries, setPatientSummaries] = useState<
    InternalPatientSummary[] | undefined
  >([]);

  const disabled = summariesLoading || project?.patientCount === 0;
  const [patientMaxMode, setPatientMaxMode] = useState(false);

  const updateProjectContext = useCallback(
    (data: InternalPatientSummary[] | undefined) => {
      if (dispatch !== null) {
        dispatch({
          type: "update",
          project: {
            patientSummaries: data,
            updatePatientSummaries,
          },
        });
      }
    },
    // eslint-disable-next-line
    [dispatch, project],
  );

  const updatePatientSummaries = useCallback(() => {
    fetchSummaries(`${projectsPrefix}/${projectId}/patients`, {
      postResponseAction: (data) => {
        setPatientSummaries(data);
        updateProjectContext(data);
      },
    });
  }, [fetchSummaries, projectId, updateProjectContext]);

  const {
    data: projectFlowsheetGroups,
    fetch: fetchProjectFlowsheetGroups,
    isLoading: projectFlowsheetGroupsLoading,
  } = useFetch<ProjectFlowsheetGroup[]>();

  useEffect(() => {
    if (project && !projectFlowsheetGroupsLoading && !projectFlowsheetGroups) {
      void fetchProjectFlowsheetGroups(
        `${apiPrefix}/projects/${project.id}/flowsheets`,
        {
          postResponseAction: (data) => {
            if (dispatch !== null) {
              dispatch({
                type: "update",
                project: {
                  flowsheetGroups: data,
                },
              });
            }
          },
        },
      );
    }
  }, [
    dispatch,
    fetchProjectFlowsheetGroups,
    project,
    projectFlowsheetGroups,
    projectFlowsheetGroupsLoading,
  ]);

  const {
    data: projectLabGroups,
    fetch: fetchProjectLabGroups,
    isLoading: projectLabGroupsLoading,
  } = useFetch<ProjectLabGroup[]>();

  useEffect(() => {
    if (project && !projectLabGroupsLoading && !projectLabGroups) {
      void fetchProjectLabGroups(`${apiPrefix}/projects/${project.id}/labs`, {
        postResponseAction: (data) => {
          if (dispatch !== null) {
            dispatch({
              type: "update",
              project: {
                labGroups: data,
              },
            });
          }
        },
      });
    }
  }, [
    dispatch,
    fetchProjectLabGroups,
    project,
    projectLabGroups,
    projectLabGroupsLoading,
  ]);

  const fetchProjectData = useCallback(async () => {
    fetchProject(`${projectsPrefix}/${projectId}`, {
      responseManipulationFunction: (data) => {
        return convertProjectToProjectContext(data, dispatch);
      },
      postResponseAction: () => {
        setPatientSummaries(undefined);
      },
    });
    resetProjectSummary();
    setProjectSummary({});
  }, [dispatch, fetchProject, projectId, resetProjectSummary]);

  const pollApiEndpoint = useCallback(
    async (projectId: string | undefined) => {
      return new Promise<ProjectSummary>((resolve, reject) => {
        fetchProjectSummary(`${apiPrefix}/projects/${projectId}/summary`, {
          responseManipulationFunction: (data) => {
            if (data) {
              resolve(data);
              return data;
            } else {
              reject(new Error("No data returned"));
              return data;
            }
          },
        });
      });
    },
    [fetchProjectSummary],
  );

  const [patient, patientDispatch] = useReducer(
    patientReducer,
    emptyPatientContext,
  );

  function updatePatientSummary(
    summary: InternalPatientSummary,
    attributes: PatientAttribute[],
  ) {
    const aeList = attributes.filter(
      (a) => a.attributeTypeId === ATTRIBUTE_TYPES.ADVERSE_EVENT,
    );
    summary.adverseEvents = aeList && aeList.length > 0;

    const perfList = attributes.filter(
      (a) => a.attributeTypeId === ATTRIBUTE_TYPES.PERFORMANCE,
    );
    summary.performance = perfList && perfList.length > 0;

    const cbList = attributes.filter(
      (a) => a.attributeTypeId === ATTRIBUTE_TYPES.CLINICAL_BENEFIT,
    );
    summary.clinicalBenefit = cbList && cbList.length > 0;
    return summary;
  }

  useEffect(() => {
    if (patient.attributesUpdated) {
      //This is to show the categorization icons in the patient list
      const newSummaries = patientSummaries?.map((s) => {
        return s.id === patient.id
          ? updatePatientSummary(s, patient.attributes)
          : s;
      });
      setPatientSummaries(newSummaries);
      patientDispatch({
        type: "update",
        patient: { ...patient, attributesUpdated: false },
      });
    }
    // forcing this to only run when the patient attributes are updated
    // eslint-disable-next-line
  }, [patient.attributesUpdated]);

  function forceTableUpdate() {
    patientDispatch({ type: "forceTableRefresh" });
  }

  function refreshProjectData() {
    fetchProjectData();
    updatePatientSummaries();
  }

  useEffect(() => {
    if (!projectLoading && !project?.id) {
      fetchProjectData();
    }
  }, [projectId, fetchProjectData, projectLoading, project?.id]);

  useEffect(() => {
    if (
      !projectLoading &&
      !!project?.id &&
      !projectSummaryLoading &&
      projectSummary &&
      !!projectSummary.totalPatients &&
      (!patientSummaries || patientSummaries.length === 0)
    ) {
      updatePatientSummaries();
    }
  }, [
    projectSummaryLoading,
    projectSummary,
    patientSummaries,
    projectLoading,
    project,
    updatePatientSummaries,
  ]);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 5;
    const timeout = 30_000; // 30 seconds

    const poll = () => {
      pollApiEndpoint(project?.id)
        .then((response) => {
          if (response !== undefined) {
            if (response?.codeUpdateStatus === "SUCCESS") {
              if (!disabled) {
                setDisableRefineButton(false);
              }
              setShowCodeUpdateFailureAlert(false);
              setShowPollingTimeoutAlert(false);
              setProjectSummary(
                projectSummaryError || !response.summary
                  ? { totalPatients: 0 }
                  : response.summary,
              );
            } else if (response.codeUpdateStatus === "FAILURE") {
              setDisableRefineButton(true);
              setShowCodeUpdateFailureAlert(true);
            } else if (retryCount < maxRetries) {
              retryCount++;
              pollTimer.current = setTimeout(poll, timeout);
            } else {
              console.error("Polling failed after maximum retries");
              setDisableRefineButton(true);
              setShowPollingTimeoutAlert(true);
            }
          }
        })
        .catch((error) => {
          console.error("Polling error:", error);
          setDisableRefineButton(true);
        });
    };

    if (
      !projectLoading &&
      !!project?.id &&
      !projectSummaryLoading &&
      projectSummaryData === undefined
    ) {
      poll();
    }
  }, [
    pollApiEndpoint,
    project,
    disabled,
    projectSummaryData,
    projectSummaryLoading,
    projectSummary,
    projectSummaryError,
    projectLoading,
  ]);

  useEffect(
    () => () => {
      clearTimeout(pollTimer.current);
    },
    [],
  );

  return (
    <div className="App">
      <NavBar />
      {!project ? (
        <div className="details-container">
          <CircularProgress />
          <Footer />
        </div>
      ) : (
        <div className="details-container">
          {project && project.isLocked && <ProjectLockedBanner />}
          {!patientMaxMode && (
            <ProjectHeader
              project={project}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              refreshData={refreshProjectData}
              disabled={disabled}
              patientsLoading={projectSummaryLoading || summariesLoading}
              patientsError={
                summariesStatus !== null && summariesStatus !== 200
              }
              disableRefineButton={disableRefineButton}
              showCodeUpdateFailureAlert={showCodeUpdateFailureAlert}
              showPollingTimeoutAlert={showPollingTimeoutAlert}
              forceTableUpdate={forceTableUpdate}
            />
          )}
          <Divider />
          {selectedTab === 0 && (
            <Overview project={project} projectSummary={projectSummary} />
          )}
          {selectedTab === 1 && (
            <PatientContext.Provider value={patient}>
              <PatientDispatchContext.Provider value={patientDispatch}>
                <Patients
                  patientMaxMode={patientMaxMode}
                  setPatientMaxMode={setPatientMaxMode}
                />
              </PatientDispatchContext.Provider>
            </PatientContext.Provider>
          )}
          <Footer />
        </div>
      )}
    </div>
  );
}
